import React, { useCallback, useEffect, useMemo, useState } from 'react';
import * as XLSX from 'xlsx';
import { api } from '../../services/api';
import { useUserPrefs } from '../../Components/context/UserPrefs';
import { Modal } from '../../Components/Modal/Modal';
import SignaturePad from '../../Components/Common/SignaturePad';
import recordOperationsDownload from '../../utils/recordOperationsDownload';
import { generateNoveltyPdfBlob } from '../../Components/print/printNovelty';

function todayStr() {
  const d = new Date();
  const y = d.getFullYear();
  const m = String(d.getMonth() + 1).padStart(2, '0');
  const day = String(d.getDate()).padStart(2, '0');
  return `${y}-${m}-${day}`;
}

function onlyDigits(value) {
  return String(value || '').replace(/\D+/g, '');
}

function computeMinutes(date, startTime, endTime) {
  const d = String(date || '').trim();
  const s = String(startTime || '').trim();
  const e = String(endTime || '').trim();
  if (!d || !s || !e) return 0;
  const start = new Date(`${d}T${s}:00`);
  const end = new Date(`${d}T${e}:00`);
  if (Number.isNaN(start.getTime()) || Number.isNaN(end.getTime())) return 0;
  if (end < start) end.setDate(end.getDate() + 1);
  return Math.max(0, Math.round((end.getTime() - start.getTime()) / 60000));
}

// Horario laboral estándar: lunes a jueves 7:30-12:30 y 2:00pm-5:30pm
// (8.5h); viernes 7:30-12:30 y 2:00pm-4:00pm (7h); sábado y domingo no
// laboran. Se usa para novedades de varios días (fecha inicio distinta a
// fecha fin) — para un solo día se sigue usando hora inicio/fin exactas.
function minutesForWeekday(dayOfWeek) {
  if (dayOfWeek === 0 || dayOfWeek === 6) return 0; // domingo, sábado
  if (dayOfWeek === 5) return 7 * 60; // viernes
  return 8.5 * 60; // lunes a jueves
}

function computeBusinessDuration(startDateStr, endDateStr) {
  const s = String(startDateStr || '').trim();
  const e = String(endDateStr || '').trim();
  if (!s || !e) return { minutes: 0, days: 0 };
  const start = new Date(`${s}T00:00:00`);
  const end = new Date(`${e}T00:00:00`);
  if (Number.isNaN(start.getTime()) || Number.isNaN(end.getTime()) || end < start) return { minutes: 0, days: 0 };
  let minutes = 0;
  let days = 0;
  const cur = new Date(start);
  while (cur <= end) {
    const m = minutesForWeekday(cur.getDay());
    if (m > 0) {
      minutes += m;
      days += 1;
    }
    cur.setDate(cur.getDate() + 1);
  }
  return { minutes, days };
}

function minutesToLabel(minutes) {
  const total = Number(minutes || 0) || 0;
  const h = Math.floor(total / 60);
  const m = total % 60;
  return `${h}h ${String(m).padStart(2, '0')}m`;
}

function minutesToHours(minutes) {
  const total = Number(minutes || 0) || 0;
  return Math.round((total / 60) * 100) / 100;
}

// Normaliza el modo de compensación de registros ya guardados (incluye
// valores históricos previos a unificar este campo con "Tipo de novedad").
// Los registros guardados antes de este cambio (que decían "No remunerado"
// para cualquier tipo, incluido un simple Permiso) no se tocan en la base
// de datos: siguen mostrando el valor que ya tenían.
function normalizeCompensationMode(value) {
  const raw = String(value || '').trim();
  const low = raw.toLowerCase();
  if (!raw) return 'No remunerado';
  if (low.includes('compensatorio')) return 'Compensatorio';
  if (low.includes('diligencia personal')) return 'Diligencia personal';
  if (low.includes('diligencia medica') || low.includes('diligencia médica')) return 'Diligencia medica';
  if (low.includes('no remunerado')) return 'No remunerado';
  if (low.includes('remunerado')) return 'Remunerado';
  return 'No remunerado';
}

// El modo de compensación ya no lo elige el usuario en el formulario: se
// deriva del tipo de novedad. "Compensatorio" descuenta horas extra
// aprobadas; "Licencia no remunerada" es la única que no se paga; el
// resto de tipos (Permiso, Incapacidad, Calamidad, Cita medica, etc.) se
// registra como "Remunerado" — "No remunerado" queda exclusivo de
// Licencia no remunerada. El backend vuelve a calcular esto mismo, así
// que este valor es solo para mostrarlo en pantalla antes de guardar.
function deriveCompensationMode(noveltyType) {
  const type = String(noveltyType || '').trim();
  if (type === 'Compensatorio') return 'Compensatorio';
  if (type === 'Licencia no remunerada') return 'No remunerado';
  return 'Remunerado';
}

function emptyForm() {
  return {
    collaborator_person_id: '',
    collaborator_name: '',
    collaborator_dni: '',
    immediate_boss_id: '',
    immediate_boss_name: '',
    novelty_date_start: todayStr(),
    novelty_date_end: todayStr(),
    novelty_type: 'Permiso',
    start_time: '',
    end_time: '',
    reason: '',
    incapacity_type: '',
    retiro_motivo: '',
  };
}

// Los tipos de novedad vienen del catálogo administrable (novelty_type_config,
// acción novelty_types): los 9 del FT-GTH-022 más los propios de Talento Humano.

const INCAPACITY_TYPE_OPTIONS = ['Común', 'Laboral'];
const RETIRO_MOTIVO_OPTIONS = ['Renuncia', 'Terminación de contrato'];

async function urlToDataUrl(url) {
  const res = await fetch(url);
  const blob = await res.blob();
  return new Promise((resolve, reject) => {
    const reader = new FileReader();
    reader.onloadend = () => resolve(String(reader.result || ''));
    reader.onerror = reject;
    reader.readAsDataURL(blob);
  });
}

// Modal de aprobación del jefe inmediato: carga automáticamente su firma
// guardada en el perfil (people.signature, buscada por su propia cédula) —
// mismo patrón ya usado en PreopPendingAuthorizations.jsx (SignRow) — con
// SignaturePad como respaldo si no tiene una guardada.
function NoveltyApproveModal({ row, onClose, onDone }) {
  const { currentUser } = useUserPrefs();
  const [dataUrl, setDataUrl] = useState('');
  const [loadingSaved, setLoadingSaved] = useState(false);
  const [savedInfo, setSavedInfo] = useState('');
  const [notes, setNotes] = useState('');
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState('');

  useEffect(() => {
    let alive = true;
    const cedula = String(currentUser?.responsibleCedula || currentUser?.responsible_cedula || '').trim();
    if (!cedula) {
      setSavedInfo('Tu usuario no tiene cédula asociada, así que no se puede buscar tu firma guardada. Puedes dibujarla abajo.');
      return undefined;
    }
    (async () => {
      setLoadingSaved(true);
      try {
        const res = await api.people.get({ dni: cedula, clean: 1 });
        const person = Array.isArray(res?.data) ? res.data[0] : res?.data;
        if (!alive) return;
        if (!person?.signature) {
          setSavedInfo('No tienes una firma guardada en tu perfil. Puedes dibujarla abajo.');
        } else {
          const du = await urlToDataUrl(person.signature);
          if (alive) setDataUrl(du);
        }
      } catch (_) {
        if (alive) setSavedInfo('No se pudo cargar tu firma guardada. Puedes dibujarla abajo.');
      }
      if (alive) setLoadingSaved(false);
    })();
    return () => { alive = false; };
  }, [currentUser?.responsibleCedula, currentUser?.responsible_cedula]);

  const submit = async () => {
    if (!dataUrl) { setError('Dibuja tu firma antes de continuar.'); return; }
    setSaving(true);
    setError('');
    try {
      const res = await api.accounting.overtimeExtras.noveltyApprove({ id: row.id, signature_base64: dataUrl, notes: notes.trim() });
      const sent = Number(res?.notified?.sent || 0);
      window.alert(sent > 0 ? `Novedad aprobada. Se notificó por correo a ${row.collaboratorName || 'el colaborador'} y a Recursos Humanos.` : 'Novedad aprobada. (No se encontró correo del colaborador para notificarle.)');
      onDone();
    } catch (e) {
      setError(String(e?.message || 'No fue posible aprobar la novedad.').replace(/^HTTP \d+:\s*/, ''));
    } finally {
      setSaving(false);
    }
  };

  return (
    <Modal isOpen onClose={onClose} title={`Aprobar novedad — ${row.collaboratorName || ''}`} size="max-w-lg">
      <div className="space-y-3">
        <p className="text-xs text-gray-500">Al aprobar se le notifica por correo al colaborador (con copia a Recursos Humanos), incluyendo la observación si la escribes.</p>
        <div className="text-sm text-gray-600">
          {row.noveltyType} · {row.noveltyDate}{row.noveltyDateEnd && row.noveltyDateEnd !== row.noveltyDate ? ` a ${row.noveltyDateEnd}` : ''}
        </div>
        <div>
          <label className="block text-sm font-medium mb-1">Tu firma</label>
          {loadingSaved ? (
            <div className="text-xs text-gray-500">Buscando tu firma guardada...</div>
          ) : savedInfo ? (
            <div className="text-xs text-gray-500 mb-1">{savedInfo}</div>
          ) : null}
          <SignaturePad onChange={setDataUrl} height={110} />
        </div>
        <div>
          <label className="block text-sm font-medium mb-1">Observación (opcional)</label>
          <textarea
            value={notes}
            onChange={(e) => setNotes(e.target.value)}
            className="w-full border rounded px-3 py-2 min-h-[70px]"
          />
        </div>
        {error ? <div className="text-sm text-red-700 bg-red-50 border border-red-200 rounded px-3 py-2">{error}</div> : null}
        <div className="flex items-center justify-end gap-2">
          <button type="button" onClick={onClose} className="px-4 py-2 rounded bg-gray-100 text-gray-700 border border-gray-300">Cancelar</button>
          <button
            type="button"
            onClick={submit}
            disabled={saving}
            className="px-4 py-2 rounded bg-green-600 text-white hover:bg-green-700 disabled:opacity-60"
          >
            {saving ? 'Firmando...' : 'Firmar y aprobar'}
          </button>
        </div>
      </div>
    </Modal>
  );
}

// Modal de rechazo del jefe inmediato: sin firma, pero con motivo
// obligatorio, que se le envía por correo al colaborador (copia a RRHH).
function NoveltyBossRejectModal({ row, onClose, onDone }) {
  const [notes, setNotes] = useState('');
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState('');

  const submit = async () => {
    if (!notes.trim()) { setError('Escribe el motivo del rechazo: se le enviará al colaborador.'); return; }
    setSaving(true);
    setError('');
    try {
      const res = await api.accounting.overtimeExtras.noveltyBossReject({ id: row.id, notes: notes.trim() });
      const sent = Number(res?.notified?.sent || 0);
      window.alert(sent > 0 ? `Novedad rechazada. Se notificó el motivo por correo a ${row.collaboratorName || 'el colaborador'} y a Recursos Humanos.` : 'Novedad rechazada. (No se encontró correo del colaborador para notificarle.)');
      onDone();
    } catch (e) {
      setError(String(e?.message || 'No fue posible rechazar la novedad.').replace(/^HTTP \d+:\s*/, ''));
    } finally {
      setSaving(false);
    }
  };

  return (
    <Modal isOpen onClose={onClose} title={`Rechazar novedad — ${row.collaboratorName || ''}`} size="max-w-lg">
      <div className="space-y-3">
        <div className="text-sm text-gray-600">
          {row.noveltyType} · {row.noveltyDate}{row.noveltyDateEnd && row.noveltyDateEnd !== row.noveltyDate ? ` a ${row.noveltyDateEnd}` : ''}
        </div>
        <div>
          <label className="block text-sm font-medium mb-1">Motivo del rechazo * <span className="font-normal text-gray-500">(se le envía al colaborador)</span></label>
          <textarea
            value={notes}
            onChange={(e) => setNotes(e.target.value)}
            className="w-full border rounded px-3 py-2 min-h-[70px]"
          />
        </div>
        {error ? <div className="text-sm text-red-700 bg-red-50 border border-red-200 rounded px-3 py-2">{error}</div> : null}
        <div className="flex items-center justify-end gap-2">
          <button type="button" onClick={onClose} className="px-4 py-2 rounded bg-gray-100 text-gray-700 border border-gray-300">Cancelar</button>
          <button
            type="button"
            onClick={submit}
            disabled={saving}
            className="px-4 py-2 rounded bg-red-600 text-white hover:bg-red-700 disabled:opacity-60"
          >
            {saving ? 'Rechazando...' : 'Rechazar'}
          </button>
        </div>
      </div>
    </Modal>
  );
}

// Modal de recepción por Gestión Humana: misma mecánica de firma que la del
// jefe inmediato (carga su propia firma guardada, con SignaturePad como
// respaldo), pero solo para quien tenga el permiso novelty_hr_can_receive.
function NoveltyHrReceiveModal({ row, onClose, onDone }) {
  const { currentUser } = useUserPrefs();
  const [dataUrl, setDataUrl] = useState('');
  const [loadingSaved, setLoadingSaved] = useState(false);
  const [savedInfo, setSavedInfo] = useState('');
  const [notes, setNotes] = useState('');
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState('');

  useEffect(() => {
    let alive = true;
    const cedula = String(currentUser?.responsibleCedula || currentUser?.responsible_cedula || '').trim();
    if (!cedula) {
      setSavedInfo('Tu usuario no tiene cédula asociada, así que no se puede buscar tu firma guardada. Puedes dibujarla abajo.');
      return undefined;
    }
    (async () => {
      setLoadingSaved(true);
      try {
        const res = await api.people.get({ dni: cedula, clean: 1 });
        const person = Array.isArray(res?.data) ? res.data[0] : res?.data;
        if (!alive) return;
        if (!person?.signature) {
          setSavedInfo('No tienes una firma guardada en tu perfil. Puedes dibujarla abajo.');
        } else {
          const du = await urlToDataUrl(person.signature);
          if (alive) setDataUrl(du);
        }
      } catch (_) {
        if (alive) setSavedInfo('No se pudo cargar tu firma guardada. Puedes dibujarla abajo.');
      }
      if (alive) setLoadingSaved(false);
    })();
    return () => { alive = false; };
  }, [currentUser?.responsibleCedula, currentUser?.responsible_cedula]);

  const submit = async () => {
    if (!dataUrl) { setError('Dibuja tu firma antes de continuar.'); return; }
    setSaving(true);
    setError('');
    try {
      await api.accounting.overtimeExtras.noveltyHrReceive({ id: row.id, signature_base64: dataUrl, notes: notes.trim() });
      onDone();
    } catch (e) {
      setError(e?.message || 'No fue posible recibir la novedad.');
    } finally {
      setSaving(false);
    }
  };

  return (
    <Modal isOpen onClose={onClose} title={`Recibir novedad — ${row.collaboratorName || ''}`} size="max-w-lg">
      <div className="space-y-3">
        <div className="text-sm text-gray-600">
          {row.noveltyType} · {row.noveltyDate}{row.noveltyDateEnd && row.noveltyDateEnd !== row.noveltyDate ? ` a ${row.noveltyDateEnd}` : ''}
        </div>
        <div>
          <label className="block text-sm font-medium mb-1">Tu firma</label>
          {loadingSaved ? (
            <div className="text-xs text-gray-500">Buscando tu firma guardada...</div>
          ) : savedInfo ? (
            <div className="text-xs text-gray-500 mb-1">{savedInfo}</div>
          ) : null}
          <SignaturePad onChange={setDataUrl} height={110} />
        </div>
        <div>
          <label className="block text-sm font-medium mb-1">Observación (opcional)</label>
          <textarea
            value={notes}
            onChange={(e) => setNotes(e.target.value)}
            className="w-full border rounded px-3 py-2 min-h-[70px]"
          />
        </div>
        {error ? <div className="text-sm text-red-700 bg-red-50 border border-red-200 rounded px-3 py-2">{error}</div> : null}
        <div className="flex items-center justify-end gap-2">
          <button type="button" onClick={onClose} className="px-4 py-2 rounded bg-gray-100 text-gray-700 border border-gray-300">Cancelar</button>
          <button
            type="button"
            onClick={submit}
            disabled={saving}
            className="px-4 py-2 rounded bg-green-600 text-white hover:bg-green-700 disabled:opacity-60"
          >
            {saving ? 'Firmando...' : 'Firmar y recibir'}
          </button>
        </div>
      </div>
    </Modal>
  );
}

// Modal de rechazo por Gestión Humana: la novedad vuelve a Pendiente para
// que el trabajador la vuelva a gestionar, así que el motivo es obligatorio.
function NoveltyHrRejectModal({ row, onClose, onDone }) {
  const [notes, setNotes] = useState('');
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState('');

  const submit = async () => {
    if (!notes.trim()) { setError('Debes indicar el motivo del rechazo.'); return; }
    setSaving(true);
    setError('');
    try {
      await api.accounting.overtimeExtras.noveltyHrReject({ id: row.id, notes: notes.trim() });
      onDone();
    } catch (e) {
      setError(e?.message || 'No fue posible rechazar la novedad.');
    } finally {
      setSaving(false);
    }
  };

  return (
    <Modal isOpen onClose={onClose} title={`Rechazar novedad — ${row.collaboratorName || ''}`} size="max-w-lg">
      <div className="space-y-3">
        <div className="text-sm text-gray-600">
          {row.noveltyType} · {row.noveltyDate}{row.noveltyDateEnd && row.noveltyDateEnd !== row.noveltyDate ? ` a ${row.noveltyDateEnd}` : ''}
        </div>
        <div className="text-xs text-amber-700 bg-amber-50 border border-amber-200 rounded px-3 py-2">
          La novedad volverá a estado Pendiente y se le avisará por correo al jefe inmediato para que el trabajador la vuelva a gestionar.
        </div>
        <div>
          <label className="block text-sm font-medium mb-1">Motivo (obligatorio)</label>
          <textarea
            value={notes}
            onChange={(e) => setNotes(e.target.value)}
            className="w-full border rounded px-3 py-2 min-h-[70px]"
          />
        </div>
        {error ? <div className="text-sm text-red-700 bg-red-50 border border-red-200 rounded px-3 py-2">{error}</div> : null}
        <div className="flex items-center justify-end gap-2">
          <button type="button" onClick={onClose} className="px-4 py-2 rounded bg-gray-100 text-gray-700 border border-gray-300">Cancelar</button>
          <button
            type="button"
            onClick={submit}
            disabled={saving}
            className="px-4 py-2 rounded bg-red-600 text-white hover:bg-red-700 disabled:opacity-60"
          >
            {saving ? 'Rechazando...' : 'Rechazar'}
          </button>
        </div>
      </div>
    </Modal>
  );
}

// Comentario/opinión de Gestión Humana sobre la novedad: a diferencia de
// hr_review_notes (solo se guarda al aprobar/recibir/rechazar), este es
// libre y editable en cualquier momento sin importar el estado de la
// novedad — para que RH pueda dejar su observación aunque el jefe
// inmediato ya la haya aprobado o rechazado.
function NoveltyHrCommentBox({ row, canEdit, onSaved }) {
  const [value, setValue] = useState(row.hrComment || '');
  const [editing, setEditing] = useState(false);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState('');

  useEffect(() => {
    setValue(row.hrComment || '');
    setEditing(false);
    setError('');
  }, [row.id, row.hrComment]);

  if (!canEdit && !row.hrComment) return null;

  const save = async () => {
    setSaving(true);
    setError('');
    try {
      await api.accounting.overtimeExtras.noveltyHrComment({ id: row.id, comment: value.trim() });
      setEditing(false);
      await onSaved();
    } catch (e) {
      setError(e?.message || 'No fue posible guardar el comentario.');
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="bg-white rounded-lg border p-4">
      <div className="text-sm font-semibold mb-2">Comentario de Gestión Humana</div>
      {canEdit && editing ? (
        <div className="space-y-2">
          <textarea
            value={value}
            onChange={(e) => setValue(e.target.value)}
            rows={3}
            className="w-full border rounded px-3 py-2 text-sm"
            placeholder="Opinión u observación de Gestión Humana sobre esta novedad, sin importar su estado"
          />
          {error && <div className="text-xs text-red-700 bg-red-50 border border-red-200 rounded px-2 py-1">{error}</div>}
          <div className="flex items-center gap-2">
            <button
              type="button"
              onClick={save}
              disabled={saving}
              className="px-3 py-1.5 rounded bg-gray-800 text-white text-sm hover:bg-black disabled:opacity-60"
            >
              {saving ? 'Guardando...' : 'Guardar comentario'}
            </button>
            <button
              type="button"
              onClick={() => { setEditing(false); setValue(row.hrComment || ''); setError(''); }}
              className="px-3 py-1.5 rounded border bg-white text-sm hover:bg-gray-50"
            >
              Cancelar
            </button>
          </div>
        </div>
      ) : (
        <div className="space-y-2">
          <div className="text-sm text-gray-800 whitespace-pre-wrap">
            {row.hrComment || <span className="text-gray-400">Sin comentario todavía.</span>}
          </div>
          {row.hrComment && row.hrCommentByName && (
            <div className="text-xs text-gray-500">{row.hrCommentByName}{row.hrCommentAt ? ` · ${row.hrCommentAt}` : ''}</div>
          )}
          {canEdit && (
            <button
              type="button"
              onClick={() => setEditing(true)}
              className="px-2.5 py-1 rounded border bg-white hover:bg-gray-50 text-sm"
            >
              {row.hrComment ? 'Editar comentario' : 'Agregar comentario'}
            </button>
          )}
        </div>
      )}
    </div>
  );
}

// ===================== FT-GTH-022 (oct-2026) =====================
// Pestañas: Mis solicitudes, Nueva solicitud, Gestión y aprobación,
// Historial y Configuración (Talento Humano). Las acciones de cada fila las
// decide el servidor (can_*); la pantalla solo las muestra.

const STATUS_STYLE = {
  Borrador: 'bg-gray-50 text-gray-700 border-gray-200',
  Pendiente: 'bg-yellow-50 text-yellow-800 border-yellow-200',
  'Pendiente director': 'bg-amber-50 text-amber-800 border-amber-200',
  Aprobada: 'bg-blue-50 text-blue-700 border-blue-200',
  Recibida: 'bg-green-50 text-green-700 border-green-200',
  Rechazada: 'bg-red-50 text-red-700 border-red-200',
  Devuelta: 'bg-orange-50 text-orange-800 border-orange-200',
  Cancelada: 'bg-gray-100 text-gray-500 border-gray-200',
};
const cleanErr = (e, fb) => String(e?.message || fb).replace(/^HTTP \d+:\s*/, '');
const fmtDT = (v) => (v ? String(v).slice(0, 16).replace('T', ' ') : '-');
const EVENT_LABELS = {
  crear_borrador: 'Guardó borrador', enviar: 'Envió la solicitud', reenviar: 'Reenvió con ajustes', editar: 'Editó la solicitud',
  aprobar_jefe: 'Aprobó (jefe inmediato)', rechazar_jefe: 'Rechazó (jefe inmediato)', devolver_jefe: 'Devolvió para ajustes (jefe inmediato)',
  aprobar_director: 'Autorizó (director/coordinador)', rechazar_director: 'Rechazó (director/coordinador)', devolver_director: 'Devolvió (director/coordinador)',
  recibir_th: 'Registró el recibido (Talento Humano)', devolver_th: 'Devolvió (Talento Humano)', cancelar: 'Canceló la solicitud',
  adjuntar_soporte: 'Adjuntó soporte', generar_pdf: 'Generó el PDF', notificar: 'Notificación',
};

function fileToDataUrl(file) {
  return new Promise((resolve, reject) => {
    const r = new FileReader();
    r.onloadend = () => resolve(String(r.result || ''));
    r.onerror = reject;
    r.readAsDataURL(file);
  });
}

function StatusPill({ row }) {
  return (
    <span className={`inline-flex px-2 py-1 rounded-full border text-xs whitespace-nowrap ${STATUS_STYLE[row.status] || STATUS_STYLE.Pendiente}`}>
      {row.statusLabel || row.status}
    </span>
  );
}

function NoveltyReturnModal({ row, onClose, onDone }) {
  const [notes, setNotes] = useState('');
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState('');
  const submit = async () => {
    if (!notes.trim()) { setError('Indica qué debe ajustar el colaborador.'); return; }
    setSaving(true);
    setError('');
    try {
      await api.accounting.overtimeExtras.noveltyBossReturn({ id: row.id, notes: notes.trim() });
      window.alert('Solicitud devuelta para ajustes. Se notificó al colaborador.');
      onDone();
    } catch (e) {
      setError(cleanErr(e, 'No fue posible devolver la solicitud.'));
    } finally {
      setSaving(false);
    }
  };
  return (
    <Modal isOpen onClose={onClose} title={`Devolver para ajustes — ${row.radicado || ''}`} size="max-w-lg">
      <div className="space-y-3">
        <label className="block text-sm font-medium" htmlFor="ret-notes">Qué debe ajustar * <span className="font-normal text-gray-500">(se le envía al colaborador)</span></label>
        <textarea id="ret-notes" value={notes} onChange={(e) => setNotes(e.target.value)} className="w-full border rounded px-3 py-2 min-h-[80px]" />
        {error && <div className="text-sm text-red-700 bg-red-50 border border-red-200 rounded px-3 py-2">{error}</div>}
        <div className="flex justify-end gap-2">
          <button type="button" onClick={onClose} className="px-4 py-2 rounded bg-gray-100 border border-gray-300">Cancelar</button>
          <button type="button" onClick={submit} disabled={saving} className="px-4 py-2 rounded bg-orange-600 text-white disabled:opacity-60">{saving ? 'Devolviendo…' : 'Devolver'}</button>
        </div>
      </div>
    </Modal>
  );
}

function NoveltyDirectorModal({ row, onClose, onDone }) {
  const [decision, setDecision] = useState('approve');
  const [notes, setNotes] = useState('');
  const [dataUrl, setDataUrl] = useState('');
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState('');
  const submit = async () => {
    if (decision === 'approve' && !dataUrl) { setError('Firma para autorizar.'); return; }
    if (decision !== 'approve' && !notes.trim()) { setError('Escribe la observación: se le enviará al colaborador.'); return; }
    setSaving(true);
    setError('');
    try {
      await api.accounting.overtimeExtras.noveltyDirectorDecide({ id: row.id, decision, notes: notes.trim(), signature_base64: decision === 'approve' ? dataUrl : '' });
      window.alert('Decisión registrada. Se notificó al colaborador.');
      onDone();
    } catch (e) {
      setError(cleanErr(e, 'No fue posible registrar la decisión.'));
    } finally {
      setSaving(false);
    }
  };
  return (
    <Modal isOpen onClose={onClose} title={`Director/Coordinador — ${row.radicado || ''}`} size="max-w-lg">
      <div className="space-y-3">
        <div className="text-sm text-gray-600">{row.collaboratorName} · {row.noveltyType} · {row.noveltyDate}</div>
        <fieldset className="flex flex-wrap gap-3 text-sm">
          <legend className="sr-only">Decisión</legend>
          {[['approve', 'Autorizar'], ['return', 'Devolver para ajustes'], ['reject', 'Rechazar']].map(([k, l]) => (
            <label key={k} className="inline-flex items-center gap-1.5"><input type="radio" name="dir-dec" checked={decision === k} onChange={() => setDecision(k)} /> {l}</label>
          ))}
        </fieldset>
        {decision === 'approve' && (
          <div>
            <div className="block text-sm font-medium mb-1">Tu firma</div>
            <SignaturePad onChange={setDataUrl} height={110} />
          </div>
        )}
        <label className="block text-sm font-medium" htmlFor="dir-notes">Observación {decision === 'approve' ? '(opcional)' : '*'}</label>
        <textarea id="dir-notes" value={notes} onChange={(e) => setNotes(e.target.value)} className="w-full border rounded px-3 py-2 min-h-[70px]" />
        {error && <div className="text-sm text-red-700 bg-red-50 border border-red-200 rounded px-3 py-2">{error}</div>}
        <div className="flex justify-end gap-2">
          <button type="button" onClick={onClose} className="px-4 py-2 rounded bg-gray-100 border border-gray-300">Cancelar</button>
          <button type="button" onClick={submit} disabled={saving} className="px-4 py-2 rounded bg-gray-800 text-white disabled:opacity-60">{saving ? 'Guardando…' : 'Confirmar'}</button>
        </div>
      </div>
    </Modal>
  );
}

function SignatureBlock({ title, url, name, at, empty }) {
  return (
    <div className="border rounded-lg p-2 text-center">
      <div className="h-16 flex items-center justify-center">{url ? <img src={url} alt={`Firma ${title}`} className="max-h-16 max-w-full" /> : <span className="text-xs text-gray-400">{empty}</span>}</div>
      <div className="text-xs font-semibold border-t pt-1 mt-1">{name || '—'}</div>
      <div className="text-[11px] text-gray-500">{title}{at ? ` · ${fmtDT(at)}` : ''}</div>
    </div>
  );
}

function NoveltyDetailModal({ id, onClose, onPdf, canComment, onChanged }) {
  const [d, setD] = useState(null);
  const [error, setError] = useState('');
  useEffect(() => {
    let alive = true;
    api.accounting.overtimeExtras.noveltiesGet({ id })
      .then((res) => { if (alive) setD(res?.data || null); })
      .catch((e) => { if (alive) setError(cleanErr(e, 'No fue posible cargar la solicitud.')); });
    return () => { alive = false; };
  }, [id]);
  const openAttachment = async (a) => {
    try {
      const res = await api.accounting.overtimeExtras.noveltyAttachmentGet({ attachment_id: a.id });
      const bin = atob(res.data_base64);
      const bytes = new Uint8Array(bin.length);
      for (let i = 0; i < bin.length; i++) bytes[i] = bin.charCodeAt(i);
      const url = URL.createObjectURL(new Blob([bytes], { type: res.mime }));
      window.open(url, '_blank', 'noopener');
      setTimeout(() => URL.revokeObjectURL(url), 60000);
    } catch (e) {
      window.alert(cleanErr(e, 'No fue posible abrir el soporte.'));
    }
  };
  const rowForComment = d ? { id: d.id, hrComment: d.hr_comment || '', hrCommentByName: d.hr_comment_by_name || '', hrCommentAt: d.hr_comment_at || '' } : null;
  return (
    <Modal isOpen onClose={onClose} title={d ? `Solicitud ${d.radicado || `#${d.id}`}` : 'Solicitud'} size="max-w-3xl">
      {error && <div className="text-sm text-red-700 bg-red-50 border border-red-200 rounded px-3 py-2">{error}</div>}
      {!d && !error && <div className="text-sm text-gray-500">Cargando…</div>}
      {d && (
        <div className="space-y-4 text-sm">
          <div className="flex flex-wrap items-center justify-between gap-2">
            <StatusPill row={{ status: d.status, statusLabel: d.status_label }} />
            {d.can_pdf ? (
              <button type="button" onClick={() => onPdf(d)} className="px-3 py-1.5 rounded bg-gray-800 text-white text-sm">Generar PDF FT-GTH-022</button>
            ) : (
              <span className="text-xs text-gray-500">El documento final estará disponible cuando Talento Humano registre el recibido.</span>
            )}
          </div>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-x-6 gap-y-1">
            <div><span className="text-gray-500">Colaborador:</span> {d.collaborator_name} · CC {d.collaborator_dni}</div>
            <div><span className="text-gray-500">Cargo / Área:</span> {d.collaborator_cargo || '-'} / {d.collaborator_area || '-'}</div>
            <div><span className="text-gray-500">Tipo:</span> {d.novelty_type}{d.incapacity_type ? ` · ${d.incapacity_type}` : ''}{d.retiro_motivo ? ` · ${d.retiro_motivo}` : ''}</div>
            <div><span className="text-gray-500">Modalidad:</span> {d.compensation_mode}</div>
            <div><span className="text-gray-500">Fechas:</span> {d.novelty_date}{d.novelty_date_end && d.novelty_date_end !== d.novelty_date ? ` a ${d.novelty_date_end}` : ''}</div>
            <div><span className="text-gray-500">Horario / duración:</span> {d.start_time ? `${String(d.start_time).slice(0, 5)}–${String(d.end_time || '').slice(0, 5)}` : '-'} · {minutesToLabel(d.minutes)}</div>
            <div><span className="text-gray-500">Enviada:</span> {fmtDT(d.submitted_at)} {Number(d.extemporanea) === 1 && <span className="ml-1 text-xs px-1.5 py-0.5 rounded bg-orange-50 text-orange-800 border border-orange-200">Extemporánea</span>}</div>
            <div><span className="text-gray-500">Registrada por:</span> {d.requested_by_name || '-'}</div>
          </div>
          <div className="rounded-lg border bg-gray-50 p-3">
            <div className="text-xs font-semibold text-gray-600 mb-1">Justificación</div>
            <div className="whitespace-pre-wrap">{d.reason || '-'}</div>
            {Number(d.extemporanea) === 1 && d.extemporanea_motivo && <div className="mt-2 text-xs text-orange-800">Motivo de la solicitud extemporánea: {d.extemporanea_motivo}</div>}
            {d.returned_notes && ['Devuelta', 'Pendiente'].includes(d.status) && <div className="mt-2 text-xs text-orange-800">Ajustes solicitados: {d.returned_notes}</div>}
          </div>
          <div className="grid grid-cols-2 md:grid-cols-4 gap-2">
            <SignatureBlock title="Trabajador" url={d.collaborator_signature_url} name={d.collaborator_name} at={d.submitted_at} empty="Sin firma" />
            <SignatureBlock title="Jefe inmediato" url={d.boss_signature_url} name={d.boss_approved_by_name || d.immediate_boss_name} at={d.boss_approved_at} empty="Pendiente" />
            <SignatureBlock title="Director/Coordinador" url={d.director_signature_url} name={d.director_by_name || d.director_name} at={d.director_at} empty={Number(d.director_required) === 1 ? 'Pendiente' : 'No requerida'} />
            <SignatureBlock title="Recibido Talento Humano" url={d.hr_signature_url} name={d.hr_received_by_name} at={d.hr_received_at} empty="Pendiente" />
          </div>
          {[['Observación del jefe', d.boss_approval_notes], ['Motivo del rechazo (jefe)', d.boss_reject_notes], ['Observación del director', d.director_notes], ['Observación de Talento Humano', d.hr_review_notes]].filter(([, v]) => v).map(([k, v]) => (
            <div key={k}><span className="text-gray-500">{k}:</span> {v}</div>
          ))}
          <div>
            <div className="text-xs font-semibold text-gray-600 mb-1">Soportes</div>
            {(d.attachments || []).length === 0 ? <div className="text-gray-500 text-xs">Sin soportes.</div> : (
              <ul className="space-y-1">{d.attachments.map((a) => (
                <li key={a.id}><button type="button" onClick={() => openAttachment(a)} className="text-blue-700 hover:underline">{a.original_name}</button> <span className="text-xs text-gray-500">· {Math.ceil(Number(a.size_bytes || 0) / 1024)} KB · {a.uploaded_by_name || ''}</span></li>
              ))}</ul>
            )}
          </div>
          {d.compensation_mode === 'Compensatorio' && (d.links || []).length > 0 && (
            <div>
              <div className="text-xs font-semibold text-gray-600 mb-1">Horas extra descontadas</div>
              <ul className="text-xs">{d.links.map((l, i) => <li key={i}>{l.overtime_date} · {l.extra_type} · {Number(l.consumed_hours ?? 0)} h</li>)}</ul>
            </div>
          )}
          <div>
            <div className="text-xs font-semibold text-gray-600 mb-1">Historial</div>
            <ol className="border-l-2 border-gray-200 pl-3 space-y-1">
              {(d.events || []).map((ev, i) => (
                <li key={i} className="text-xs"><span className="font-mono text-gray-500">{fmtDT(ev.created_at)}</span> · <b>{EVENT_LABELS[ev.action] || ev.action}</b> · {ev.actor_name || '—'}{ev.actor_role ? ` (${ev.actor_role})` : ''}{ev.notes ? ` · ${ev.notes}` : ''}</li>
              ))}
              {(d.events || []).length === 0 && <li className="text-xs text-gray-500">Registro anterior al historial de eventos (oct-2026).</li>}
            </ol>
          </div>
          {rowForComment && <NoveltyHrCommentBox row={rowForComment} canEdit={canComment} onSaved={onChanged} />}
        </div>
      )}
    </Modal>
  );
}

function NoveltyTypeConfigView({ types, onSaved }) {
  const [rows, setRows] = useState(types);
  const [savingType, setSavingType] = useState('');
  useEffect(() => { setRows(types); }, [types]);
  const upd = (name, patch) => setRows((rs) => rs.map((r) => (r.type_name === name ? { ...r, ...patch } : r)));
  const save = async (r) => {
    setSavingType(r.type_name);
    try {
      await api.accounting.overtimeExtras.noveltyTypeConfigSave({
        type_name: r.type_name, anticipation_business_days: Number(r.anticipation_business_days || 0),
        requires_attachment: Number(r.requires_attachment) === 1, requires_director: Number(r.requires_director) === 1,
        director_id: r.director_id || '', director_name: r.director_name || '', active: Number(r.active) === 1,
        collaborator_selectable: Number(r.collaborator_selectable) === 1, max_reason_chars: Number(r.max_reason_chars || 2000),
      });
      onSaved();
    } catch (e) {
      window.alert(cleanErr(e, 'No fue posible guardar.'));
    } finally {
      setSavingType('');
    }
  };
  const chk = (r, key) => <input type="checkbox" checked={Number(r[key]) === 1} onChange={(e) => upd(r.type_name, { [key]: e.target.checked ? 1 : 0 })} aria-label={`${key} ${r.type_name}`} />;
  return (
    <div className="bg-white rounded-xl border overflow-x-auto">
      <div className="px-4 py-3 border-b text-sm">
        <div className="font-semibold">Configuración de tipos de novedad</div>
        <div className="text-xs text-gray-500">Anticipación en días hábiles (la nota del FT-GTH-022 pide 8 para permisos, excepto calamidad), soporte obligatorio y si requiere autorización de director/coordinador (cédula de quien autoriza).</div>
      </div>
      <table className="min-w-full text-sm">
        <thead className="bg-gray-50 text-gray-600 text-xs">
          <tr><th className="px-3 py-2 text-left">Tipo</th><th className="px-2 py-2">Activo</th><th className="px-2 py-2">Colaborador lo elige</th><th className="px-2 py-2">Días hábiles</th><th className="px-2 py-2">Soporte</th><th className="px-2 py-2">Director</th><th className="px-2 py-2 text-left">Cédula director</th><th className="px-2 py-2">Máx. justificación</th><th className="px-2 py-2" /></tr>
        </thead>
        <tbody>
          {rows.map((r) => (
            <tr key={r.type_name} className="border-t">
              <td className="px-3 py-2">{r.type_name}{Number(r.hr_only) === 1 && <span className="ml-1 text-[11px] text-gray-500">(solo TH)</span>}{!r.ft_box && Number(r.hr_only) !== 1 && <span className="ml-1 text-[11px] text-gray-500">(anterior)</span>}</td>
              <td className="px-2 py-2 text-center">{chk(r, 'active')}</td>
              <td className="px-2 py-2 text-center">{chk(r, 'collaborator_selectable')}</td>
              <td className="px-2 py-2 text-center"><input type="number" min="0" max="60" value={r.anticipation_business_days} onChange={(e) => upd(r.type_name, { anticipation_business_days: e.target.value })} className="w-16 border rounded px-1 py-0.5" aria-label={`Días ${r.type_name}`} /></td>
              <td className="px-2 py-2 text-center">{chk(r, 'requires_attachment')}</td>
              <td className="px-2 py-2 text-center">{chk(r, 'requires_director')}</td>
              <td className="px-2 py-2"><input value={r.director_id || ''} onChange={(e) => upd(r.type_name, { director_id: e.target.value, director_name: '' })} placeholder="Cédula" className="w-28 border rounded px-1 py-0.5" aria-label={`Cédula director ${r.type_name}`} /><div className="text-[11px] text-gray-500">{r.director_name || ''}</div></td>
              <td className="px-2 py-2 text-center"><input type="number" min="100" max="10000" value={r.max_reason_chars} onChange={(e) => upd(r.type_name, { max_reason_chars: e.target.value })} className="w-20 border rounded px-1 py-0.5" aria-label={`Máximo ${r.type_name}`} /></td>
              <td className="px-2 py-2"><button type="button" onClick={() => save(r)} disabled={savingType === r.type_name} className="px-2.5 py-1 rounded bg-gray-800 text-white text-xs disabled:opacity-60">{savingType === r.type_name ? '…' : 'Guardar'}</button></td>
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
}

export default function HrNoveltiesModule({ location, people = [] }) {
  const { currentUser } = useUserPrefs();
  const [view, setView] = useState('mine');
  const [meta, setMeta] = useState({ isHr: false, canReceive: false, types: [] });
  const [profile, setProfile] = useState(null);
  const [profileLoaded, setProfileLoaded] = useState(false);
  const [filters, setFilters] = useState({ from: '', to: '', q: '', status: '', type: '' });
  const [applied, setApplied] = useState(() => ({ ...filters }));
  const [rows, setRows] = useState([]);
  const [pendingCount, setPendingCount] = useState(0);
  const [loading, setLoading] = useState(false);
  const [editing, setEditing] = useState(null);
  const [form, setForm] = useState(emptyForm);
  const [forOther, setForOther] = useState(false);
  const [files, setFiles] = useState([]);
  const [needsExtemp, setNeedsExtemp] = useState(false);
  const [extempMotivo, setExtempMotivo] = useState('');
  const [saving, setSaving] = useState(false);
  const [feedback, setFeedback] = useState({ type: '', text: '' });
  const [balanceLoading, setBalanceLoading] = useState(false);
  const [balance, setBalance] = useState(null);
  const [peopleQuery, setPeopleQuery] = useState('');
  const [peopleOpen, setPeopleOpen] = useState(false);
  const [detailId, setDetailId] = useState(null);
  const [approveTarget, setApproveTarget] = useState(null);
  const [rejectTarget, setRejectTarget] = useState(null);
  const [returnTarget, setReturnTarget] = useState(null);
  const [directorTarget, setDirectorTarget] = useState(null);
  const [hrReceiveTarget, setHrReceiveTarget] = useState(null);
  const [hrRejectTarget, setHrRejectTarget] = useState(null);
  const [pdfLoadingId, setPdfLoadingId] = useState(null);

  const loadMeta = useCallback(async () => {
    try {
      const [t, p] = await Promise.all([api.accounting.overtimeExtras.noveltyTypes(), api.accounting.overtimeExtras.noveltyProfile()]);
      setMeta({ isHr: !!t?.is_hr, canReceive: !!t?.can_receive, types: Array.isArray(t?.data) ? t.data : [] });
      setProfile(p?.data || null);
    } catch (_) {
      setMeta((m) => m);
    } finally {
      setProfileLoaded(true);
    }
  }, []);
  useEffect(() => { loadMeta(); }, [loadMeta]);

  const mapRow = (r) => ({
    id: Number(r.id || 0) || 0,
    radicado: String(r.radicado || ''),
    location: r.location || '',
    collaboratorPersonId: Number(r.collaborator_person_id || 0) || 0,
    collaboratorName: String(r.collaborator_name || ''),
    collaboratorDni: String(r.collaborator_dni || ''),
    collaboratorArea: String(r.collaborator_area || ''),
    noveltyDate: String(r.novelty_date || ''),
    noveltyDateEnd: String(r.novelty_date_end || '') || String(r.novelty_date || ''),
    noveltyType: String(r.novelty_type || ''),
    compensationMode: normalizeCompensationMode(r.compensation_mode || 'Remunerado'),
    startTime: String(r.start_time || ''),
    endTime: String(r.end_time || ''),
    minutes: Number(r.minutes || 0) || 0,
    hoursDecimal: Number(r.hours_decimal || 0) || 0,
    consumedMinutes: Number(r.consumed_minutes || 0) || 0,
    consumedHours: Number(r.consumed_hours || 0) || 0,
    reason: String(r.reason || ''),
    requestedByName: String(r.requested_by_name || ''),
    createdAt: String(r.created_at || ''),
    updatedAt: String(r.updated_at || ''),
    submittedAt: String(r.submitted_at || ''),
    status: String(r.status || 'Pendiente'),
    statusLabel: String(r.status_label || r.status || ''),
    currentResponsible: String(r.current_responsible || ''),
    immediateBossId: String(r.immediate_boss_id || ''),
    immediateBossName: String(r.immediate_boss_name || ''),
    extemporanea: Number(r.extemporanea || 0) === 1,
    incapacityType: String(r.incapacity_type || ''),
    retiroMotivo: String(r.retiro_motivo || ''),
    returnedNotes: String(r.returned_notes || ''),
    canApprove: !!r.can_approve,
    canDirector: !!r.can_director,
    canHrReceive: !!r.can_hr_receive,
    canEdit: !!r.can_edit,
    canCancel: !!r.can_cancel,
    canPdf: !!r.can_pdf,
  });

  const loadRows = useCallback(async () => {
    if (view === 'new' || view === 'config') return;
    setLoading(true);
    try {
      const scope = view === 'mine' ? 'mine' : (view === 'pending' ? 'pending' : '');
      const res = await api.accounting.overtimeExtras.noveltiesList({
        location, scope, from: applied.from, to: applied.to, q: applied.q, status: applied.status, novelty_type: applied.type, limit: 1000, order: 'desc',
      });
      setRows((Array.isArray(res?.data) ? res.data : []).map(mapRow));
    } catch (e) {
      setRows([]);
      setFeedback({ type: 'error', text: cleanErr(e, 'No fue posible cargar las novedades.') });
    } finally {
      setLoading(false);
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [view, applied.from, applied.to, applied.q, applied.status, applied.type, location]);
  useEffect(() => { loadRows(); }, [loadRows]);

  const loadPendingCount = useCallback(async () => {
    try {
      const res = await api.accounting.overtimeExtras.noveltiesList({ scope: 'pending', limit: 500 });
      setPendingCount(Array.isArray(res?.data) ? res.data.length : 0);
    } catch (_) {}
  }, []);
  useEffect(() => { loadPendingCount(); }, [loadPendingCount]);

  const refreshAll = async () => { await Promise.all([loadRows(), loadPendingCount()]); };
  const afterAction = async () => {
    setApproveTarget(null); setRejectTarget(null); setReturnTarget(null); setDirectorTarget(null); setHrReceiveTarget(null); setHrRejectTarget(null);
    await refreshAll();
  };

  // ---------- Formulario ----------
  const selfMode = !forOther;
  const typeOptions = useMemo(() => meta.types.filter((t) => Number(t.active) === 1
    && ((forOther && meta.isHr) || (Number(t.collaborator_selectable) === 1 && Number(t.hr_only) !== 1))), [meta.types, forOther, meta.isHr]);
  const typeCfg = meta.types.find((t) => t.type_name === form.novelty_type) || null;
  const isFtType = !!typeCfg?.ft_box;

  // En modo propio, los datos salen de la ficha oficial (solo lectura).
  useEffect(() => {
    if (!selfMode || editing || !profile) return;
    setForm((prev) => ({
      ...prev,
      collaborator_person_id: profile.person_id ? String(profile.person_id) : '',
      collaborator_name: profile.name || '',
      collaborator_dni: profile.dni || '',
      immediate_boss_id: profile.immediate_boss_id || '',
      immediate_boss_name: profile.immediate_boss_name || '',
    }));
  }, [selfMode, editing, profile]);
  useEffect(() => {
    if (typeOptions.length && !typeOptions.some((t) => t.type_name === form.novelty_type)) {
      setForm((prev) => ({ ...prev, novelty_type: typeOptions[0].type_name }));
    }
  }, [typeOptions, form.novelty_type]);

  const normalizedPeople = useMemo(() => (Array.isArray(people) ? people : []).map((p) => {
    const dni = String(p?.dni || p?.documento_identidad || p?.documento || p?.id || '').trim();
    return {
      dbId: Number(p?._dbId ?? p?._db_id ?? p?.dbId ?? p?.db_id ?? 0) || 0,
      dni, digits: onlyDigits(dni),
      name: String(p?.name || p?.nombre_completo || p?.nombre || '').trim(),
      position: String(p?.position || p?.cargo || '').trim(),
      immediateBossId: String(p?.immediateBossId || p?.immediate_boss_id || p?.jefe_inmediato || '').trim(),
    };
  }).filter((p) => p.name || p.dni), [people]);
  const suggestedPeople = useMemo(() => {
    const q = String(peopleQuery || '').trim().toLowerCase();
    const list = q ? normalizedPeople.filter((p) => `${p.name} ${p.dni} ${p.position}`.toLowerCase().includes(q)) : normalizedPeople;
    return list.slice(0, 12);
  }, [normalizedPeople, peopleQuery]);

  useEffect(() => {
    if (selfMode) return undefined;
    const dni = onlyDigits(form.collaborator_dni);
    if (!dni) return undefined;
    const person = normalizedPeople.find((p) => p.digits === dni) || null;
    let alive = true;
    (async () => {
      const bossId = onlyDigits(person?.immediateBossId || '');
      let bossName = '';
      if (bossId) {
        try { const r = await api.people.get({ dni: bossId }); bossName = String(r?.data?.name || r?.data?.nombre_completo || '').trim(); } catch (_) {}
      }
      if (alive) setForm((prev) => ({ ...prev, immediate_boss_id: bossId, immediate_boss_name: bossName }));
    })();
    return () => { alive = false; };
  }, [selfMode, form.collaborator_dni, normalizedPeople]);

  useEffect(() => {
    const dni = onlyDigits(form.collaborator_dni);
    if (!dni || form.novelty_type !== 'Compensatorio') { setBalance(null); return undefined; }
    let alive = true;
    setBalanceLoading(true);
    api.accounting.overtimeExtras.noveltiesBalance({ location, collaborator_dni: dni })
      .then((res) => { if (alive) setBalance(res?.data || null); })
      .catch(() => { if (alive) setBalance(null); })
      .finally(() => { if (alive) setBalanceLoading(false); });
    return () => { alive = false; };
  }, [location, form.collaborator_dni, form.novelty_type]);

  const isSameDay = form.novelty_date_start === form.novelty_date_end;
  const isIncapacidad = form.novelty_type === 'Incapacidad';
  const isRetiro = form.novelty_type === 'Retiro';
  const duration = useMemo(() => (isSameDay
    ? { minutes: computeMinutes(form.novelty_date_start, form.start_time, form.end_time), days: null }
    : computeBusinessDuration(form.novelty_date_start, form.novelty_date_end)), [isSameDay, form.novelty_date_start, form.novelty_date_end, form.start_time, form.end_time]);
  const calendarDays = useMemo(() => {
    const a = new Date(`${form.novelty_date_start}T00:00:00`); const b = new Date(`${form.novelty_date_end || form.novelty_date_start}T00:00:00`);
    const d = Math.round((b - a) / 86400000) + 1;
    return Number.isFinite(d) && d > 0 ? d : 0;
  }, [form.novelty_date_start, form.novelty_date_end]);
  const maxReason = Number(typeCfg?.max_reason_chars || 2000);

  const resetForm = () => {
    setEditing(null); setForm(emptyForm()); setFiles([]); setNeedsExtemp(false); setExtempMotivo('');
    setPeopleQuery(''); setPeopleOpen(false); setFeedback({ type: '', text: '' });
  };

  const openEdit = (row) => {
    setEditing(row);
    setForOther(!(profile && onlyDigits(profile.dni) === onlyDigits(row.collaboratorDni)));
    setForm({
      collaborator_person_id: row.collaboratorPersonId ? String(row.collaboratorPersonId) : '',
      collaborator_name: row.collaboratorName, collaborator_dni: row.collaboratorDni,
      immediate_boss_id: row.immediateBossId, immediate_boss_name: row.immediateBossName,
      novelty_date_start: row.noveltyDate || todayStr(), novelty_date_end: row.noveltyDateEnd || row.noveltyDate || todayStr(),
      novelty_type: row.noveltyType, start_time: String(row.startTime || '').slice(0, 5), end_time: String(row.endTime || '').slice(0, 5),
      reason: row.reason, incapacity_type: row.incapacityType, retiro_motivo: row.retiroMotivo,
    });
    setPeopleQuery(`${row.collaboratorName} · ${row.collaboratorDni}`);
    setFiles([]); setNeedsExtemp(row.extemporanea); setExtempMotivo('');
    setFeedback(row.returnedNotes && row.status === 'Devuelta' ? { type: 'warn', text: `Ajustes solicitados: ${row.returnedNotes}` } : { type: '', text: '' });
    setView('new');
  };

  const buildPayload = () => ({
    ...(editing?.id ? { id: editing.id } : {}),
    location,
    collaborator_person_id: form.collaborator_person_id ? Number(form.collaborator_person_id) : null,
    collaborator_name: String(form.collaborator_name || '').trim(),
    collaborator_dni: onlyDigits(form.collaborator_dni),
    immediate_boss_id: onlyDigits(form.immediate_boss_id) || null,
    immediate_boss_name: String(form.immediate_boss_name || '').trim() || null,
    novelty_date: String(form.novelty_date_start || '').trim(),
    novelty_date_end: isRetiro ? String(form.novelty_date_start || '').trim() : String(form.novelty_date_end || form.novelty_date_start || '').trim(),
    novelty_type: form.novelty_type,
    compensation_mode: deriveCompensationMode(form.novelty_type),
    start_time: (isSameDay && !isRetiro) ? (String(form.start_time || '').trim() || null) : null,
    end_time: (isSameDay && !isRetiro) ? (String(form.end_time || '').trim() || null) : null,
    minutes: isRetiro ? null : (duration.minutes > 0 ? duration.minutes : null),
    reason: String(form.reason || '').trim() || null,
    incapacity_type: isIncapacidad ? (form.incapacity_type || null) : null,
    retiro_motivo: isRetiro ? (form.retiro_motivo || null) : null,
    extemporanea_motivo: extempMotivo.trim() || null,
  });

  const save = async (submit) => {
    if (saving) return; // evita envíos duplicados
    const payload = buildPayload();
    if (!payload.collaborator_name || !payload.collaborator_dni) { setFeedback({ type: 'error', text: selfMode ? 'Tu usuario no está vinculado a una ficha de colaborador. Pide a Talento Humano que la vincule.' : 'Selecciona el colaborador.' }); return; }
    if (!payload.novelty_date) { setFeedback({ type: 'error', text: 'Indica la fecha de inicio.' }); return; }
    if (payload.novelty_date_end < payload.novelty_date) { setFeedback({ type: 'error', text: 'La fecha final no puede ser anterior a la de inicio.' }); return; }
    if (isIncapacidad && !payload.incapacity_type) { setFeedback({ type: 'error', text: 'Indica el tipo de incapacidad.' }); return; }
    if (isRetiro && !payload.retiro_motivo) { setFeedback({ type: 'error', text: 'Indica el motivo del retiro.' }); return; }
    if (!isRetiro && !(Number(payload.minutes || 0) > 0)) { setFeedback({ type: 'error', text: isSameDay ? 'Indica hora de inicio y hora final.' : 'El rango de fechas no tiene días laborales.' }); return; }
    if (submit && !payload.reason) { setFeedback({ type: 'error', text: 'La justificación es obligatoria.' }); return; }
    if (payload.reason && payload.reason.length > maxReason) { setFeedback({ type: 'error', text: `La justificación supera ${maxReason} caracteres.` }); return; }
    if (submit && Number(typeCfg?.requires_attachment) === 1 && files.length === 0 && !editing) { setFeedback({ type: 'error', text: 'Este tipo requiere adjuntar el soporte.' }); return; }
    if (submit && !window.confirm('¿Enviar la solicitud? Se firmará con la firma registrada en tu ficha y pasará a tu jefe inmediato.')) return;
    setSaving(true);
    setFeedback({ type: '', text: '' });
    try {
      // 1) Guardar (borrador) para tener id; 2) subir soportes; 3) enviar.
      const draft = await api.accounting.overtimeExtras.noveltiesUpsert({ ...payload, submit: false });
      const id = Number(draft?.id || editing?.id || 0);
      for (const f of files) {
        if (f.size > 5 * 1024 * 1024) throw new Error(`"${f.name}" supera 5 MB.`);
        await api.accounting.overtimeExtras.noveltyAttachmentUpload({ id, name: f.name, data_base64: await fileToDataUrl(f) });
      }
      setFiles([]);
      if (!submit) {
        setEditing({ ...(editing || {}), id, status: 'Borrador' });
        setFeedback({ type: 'ok', text: 'Borrador guardado. Puedes enviarlo cuando esté listo.' });
        await refreshAll();
        return;
      }
      const sent = await api.accounting.overtimeExtras.noveltiesUpsert({ ...payload, id, submit: true });
      if (isRetiro) window.alert(`Retiro registrado. ${payload.collaborator_name} quedó marcado como "Retirado".`);
      resetForm();
      setFeedback({ type: 'ok', text: `Solicitud enviada con radicado ${sent?.radicado || ''}. Estado: ${sent?.status_label || 'Pendiente'}.` });
      setView('mine');
      await refreshAll();
    } catch (e) {
      const msg = cleanErr(e, 'No fue posible guardar la solicitud.');
      if (/d[ií]as h[aá]biles de anticipaci/i.test(msg)) setNeedsExtemp(true);
      setFeedback({ type: 'error', text: msg });
      await refreshAll();
    } finally {
      setSaving(false);
    }
  };

  const cancelRow = async (row) => {
    const notes = window.prompt(`¿Cancelar la solicitud ${row.radicado || ''}? Puedes indicar el motivo (opcional):`, '');
    if (notes === null) return;
    try {
      await api.accounting.overtimeExtras.noveltyCancel({ id: row.id, notes });
      await refreshAll();
    } catch (e) {
      window.alert(cleanErr(e, 'No fue posible cancelar.'));
    }
  };

  const downloadPdf = async (row) => {
    setPdfLoadingId(row.id);
    try {
      // El servidor valida estado Recibido y permisos y registra la generación.
      const res = await api.accounting.overtimeExtras.noveltyPdfData({ id: row.id });
      const blob = await generateNoveltyPdfBlob(res.data);
      const url = URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = `FT-GTH-022_${res.data.radicado || row.id}_v${res.data.pdf_version || 1}.pdf`;
      document.body.appendChild(a); a.click(); a.remove();
      setTimeout(() => URL.revokeObjectURL(url), 5000);
      void recordOperationsDownload({ fileType: 'pdf', moduleKey: 'recursos-humanos', reportName: 'FT-GTH-022 Novedad de personal' });
    } catch (e) {
      window.alert(cleanErr(e, 'No fue posible generar el PDF.'));
    } finally {
      setPdfLoadingId(null);
    }
  };

  const exportExcel = () => {
    const ws = XLSX.utils.json_to_sheet(rows.map((r) => ({
      radicado: r.radicado, colaborador: r.collaboratorName, documento: r.collaboratorDni, area: r.collaboratorArea,
      tipo: r.noveltyType, estado: r.statusLabel, responsable_actual: r.currentResponsible,
      fecha_solicitud: (r.submittedAt || r.createdAt).slice(0, 10), fecha_inicio: r.noveltyDate, fecha_fin: r.noveltyDateEnd,
      horas: r.hoursDecimal || minutesToHours(r.minutes), modalidad: r.compensationMode, extemporanea: r.extemporanea ? 'Sí' : 'No',
      horas_cruzadas_extras: r.compensationMode === 'Compensatorio' ? (r.consumedHours || minutesToHours(r.consumedMinutes)) : 0,
      justificacion: r.reason, registrado_por: r.requestedByName,
    })));
    const wb = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(wb, ws, 'Novedades');
    void recordOperationsDownload({ fileType: 'xlsx', moduleKey: 'recursos-humanos', reportName: 'Novedades RRHH' });
    XLSX.writeFile(wb, `novedades_rrhh_${new Date().toISOString().slice(0, 10)}.xlsx`);
  };

  const tabs = [
    ['mine', 'Mis solicitudes', null],
    ['new', editing ? 'Editar solicitud' : 'Nueva solicitud', null],
    ['pending', 'Gestión y aprobación', pendingCount],
    ['history', 'Historial de novedades', null],
    ...(meta.isHr ? [['config', 'Configuración', null]] : []),
  ];

  const actionBtn = 'px-2.5 py-1 rounded text-xs font-medium';
  const renderActions = (row) => (
    <div className="flex flex-wrap gap-1.5">
      <button type="button" onClick={() => setDetailId(row.id)} className={`${actionBtn} border bg-white hover:bg-gray-50`}>Ver</button>
      {row.canApprove && (<>
        <button type="button" onClick={() => setApproveTarget(row)} className={`${actionBtn} bg-green-600 text-white`}>Aprobar</button>
        <button type="button" onClick={() => setReturnTarget(row)} className={`${actionBtn} bg-orange-500 text-white`}>Devolver</button>
        <button type="button" onClick={() => setRejectTarget(row)} className={`${actionBtn} bg-red-600 text-white`}>Rechazar</button>
      </>)}
      {row.canDirector && <button type="button" onClick={() => setDirectorTarget(row)} className={`${actionBtn} bg-amber-600 text-white`}>Decidir (director)</button>}
      {row.canHrReceive && (<>
        <button type="button" onClick={() => setHrReceiveTarget(row)} className={`${actionBtn} bg-green-600 text-white`}>Recibir</button>
        <button type="button" onClick={() => setHrRejectTarget(row)} className={`${actionBtn} bg-red-600 text-white`}>Devolver (TH)</button>
      </>)}
      {row.canEdit && <button type="button" onClick={() => openEdit(row)} className={`${actionBtn} border bg-white hover:bg-gray-50`}>{row.status === 'Devuelta' ? 'Ajustar y reenviar' : 'Editar'}</button>}
      {row.canCancel && <button type="button" onClick={() => cancelRow(row)} className={`${actionBtn} border border-red-200 bg-red-50 text-red-700`}>Cancelar</button>}
      {row.canPdf && <button type="button" onClick={() => downloadPdf(row)} disabled={pdfLoadingId === row.id} className={`${actionBtn} bg-gray-800 text-white disabled:opacity-60`}>{pdfLoadingId === row.id ? 'Generando…' : 'PDF'}</button>}
    </div>
  );

  const renderList = () => (
    <div className="bg-white rounded-xl border overflow-hidden">
      {view === 'history' && (
        <div className="p-3 border-b grid grid-cols-2 md:grid-cols-6 gap-2 items-end text-sm">
          <label className="block"><span className="text-xs text-gray-600">Desde</span><input type="date" value={filters.from} onChange={(e) => setFilters((f) => ({ ...f, from: e.target.value }))} className="w-full border rounded px-2 py-1.5" /></label>
          <label className="block"><span className="text-xs text-gray-600">Hasta</span><input type="date" value={filters.to} onChange={(e) => setFilters((f) => ({ ...f, to: e.target.value }))} className="w-full border rounded px-2 py-1.5" /></label>
          <label className="block"><span className="text-xs text-gray-600">Estado</span>
            <select value={filters.status} onChange={(e) => setFilters((f) => ({ ...f, status: e.target.value }))} className="w-full border rounded px-2 py-1.5">
              <option value="">Todos</option>{Object.keys(STATUS_STYLE).map((s) => <option key={s} value={s}>{s === 'Pendiente' ? 'Pendiente jefe inmediato' : s === 'Aprobada' ? 'En revisión Talento Humano' : s === 'Recibida' ? 'Recibido' : s}</option>)}
            </select></label>
          <label className="block"><span className="text-xs text-gray-600">Tipo</span>
            <select value={filters.type} onChange={(e) => setFilters((f) => ({ ...f, type: e.target.value }))} className="w-full border rounded px-2 py-1.5">
              <option value="">Todos</option>{meta.types.map((t) => <option key={t.type_name} value={t.type_name}>{t.type_name}</option>)}
            </select></label>
          <label className="block col-span-2 md:col-span-1"><span className="text-xs text-gray-600">Buscar</span><input value={filters.q} onChange={(e) => setFilters((f) => ({ ...f, q: e.target.value }))} placeholder="Radicado, colaborador, área, jefe…" className="w-full border rounded px-2 py-1.5" /></label>
          <div className="flex gap-2">
            <button type="button" onClick={() => setApplied({ ...filters })} className="px-3 py-1.5 rounded bg-gray-800 text-white">Aplicar</button>
            {!!currentUser?.hrNoveltiesCanExport && <button type="button" onClick={exportExcel} disabled={!rows.length} className="px-3 py-1.5 rounded border disabled:opacity-50">Excel</button>}
          </div>
        </div>
      )}
      {loading ? <div className="px-4 py-10 text-center text-sm text-gray-600">Cargando…</div> : rows.length === 0 ? (
        <div className="px-4 py-10 text-center text-sm text-gray-600">{view === 'pending' ? 'No tienes solicitudes pendientes de tu acción.' : 'No hay solicitudes.'}</div>
      ) : (
        <>
          <table className="hidden lg:table min-w-full text-sm">
            <thead className="bg-gray-50 text-gray-600 text-xs"><tr>
              <th className="px-3 py-2 text-left">Radicado</th><th className="px-3 py-2 text-left">Colaborador</th><th className="px-3 py-2 text-left">Tipo</th>
              <th className="px-3 py-2 text-left">Solicitud</th><th className="px-3 py-2 text-left">Inicio</th><th className="px-3 py-2 text-left">Fin</th>
              <th className="px-3 py-2 text-left">Estado</th><th className="px-3 py-2 text-left">Responsable actual</th><th className="px-3 py-2 text-left">Actualizada</th><th className="px-3 py-2 text-left">Acciones</th>
            </tr></thead>
            <tbody>{rows.map((r) => (
              <tr key={r.id} className="border-t align-top">
                <td className="px-3 py-2 font-mono text-xs">{r.radicado || <span className="text-gray-400">#{r.id}</span>}</td>
                <td className="px-3 py-2"><div className="font-medium">{r.collaboratorName}</div><div className="text-xs text-gray-500">CC {r.collaboratorDni}</div></td>
                <td className="px-3 py-2">{r.noveltyType}{r.extemporanea && <div className="text-[11px] text-orange-700">Extemporánea</div>}</td>
                <td className="px-3 py-2 font-mono text-xs">{(r.submittedAt || r.createdAt).slice(0, 10)}</td>
                <td className="px-3 py-2 font-mono text-xs">{r.noveltyDate}</td>
                <td className="px-3 py-2 font-mono text-xs">{r.noveltyDateEnd}</td>
                <td className="px-3 py-2"><StatusPill row={r} /></td>
                <td className="px-3 py-2 text-xs">{r.currentResponsible || '—'}</td>
                <td className="px-3 py-2 font-mono text-xs">{fmtDT(r.updatedAt)}</td>
                <td className="px-3 py-2">{renderActions(r)}</td>
              </tr>
            ))}</tbody>
          </table>
          <div className="lg:hidden divide-y">{rows.map((r) => (
            <div key={r.id} className="p-3 space-y-1.5">
              <div className="flex items-center justify-between gap-2"><span className="font-mono text-xs">{r.radicado || `#${r.id}`}</span><StatusPill row={r} /></div>
              <div className="font-medium">{r.collaboratorName}</div>
              <div className="text-sm text-gray-600">{r.noveltyType} · {r.noveltyDate}{r.noveltyDateEnd !== r.noveltyDate ? ` a ${r.noveltyDateEnd}` : ''}</div>
              {r.currentResponsible && <div className="text-xs text-gray-500">Responsable: {r.currentResponsible}</div>}
              {renderActions(r)}
            </div>
          ))}</div>
        </>
      )}
    </div>
  );

  const renderForm = () => {
    const canRegisterOthers = meta.isHr || normalizedPeople.length > 0;
    return (
      <div className="bg-white rounded-xl border p-4 md:p-5 space-y-4">
        <div className="flex flex-wrap items-center justify-between gap-2">
          <div>
            <h5 className="text-sm font-semibold">{editing ? `Editar solicitud ${editing.radicado || ''}` : 'Solicitud de novedades de personal'}</h5>
            <p className="text-xs text-gray-500">Formato FT-GTH-022 · Gestión de Talento Humano</p>
          </div>
          {!editing && canRegisterOthers && (
            <label className="inline-flex items-center gap-2 text-sm"><input type="checkbox" checked={forOther} onChange={(e) => { setForOther(e.target.checked); setForm(emptyForm()); setPeopleQuery(''); }} /> Registrar para otro colaborador</label>
          )}
        </div>
        {feedback.text && (
          <div role="status" className={`rounded-lg border px-3 py-2 text-sm ${feedback.type === 'ok' ? 'bg-green-50 text-green-700 border-green-200' : feedback.type === 'warn' ? 'bg-orange-50 text-orange-800 border-orange-200' : 'bg-red-50 text-red-700 border-red-200'}`}>{feedback.text}</div>
        )}

        <fieldset className="rounded-lg border p-3">
          <legend className="px-1 text-xs font-semibold text-gray-600">A. Identificación del colaborador</legend>
          {selfMode ? (
            profileLoaded && !profile ? (
              <div className="text-sm text-red-700">Tu usuario no está vinculado a una ficha de colaborador (cédula). Pide a Talento Humano que la vincule para poder solicitar novedades.</div>
            ) : (
              <div className="grid grid-cols-1 md:grid-cols-2 gap-x-6 gap-y-1 text-sm">
                <div><span className="text-gray-500">Nombre:</span> {form.collaborator_name || '…'}</div>
                <div><span className="text-gray-500">Cédula:</span> {form.collaborator_dni || '…'}</div>
                <div><span className="text-gray-500">Cargo:</span> {profile?.cargo || '-'}</div>
                <div><span className="text-gray-500">Área/Proyecto:</span> {profile?.area || '-'}</div>
                <div className="md:col-span-2"><span className="text-gray-500">Jefe inmediato:</span> {form.immediate_boss_name || <span className="text-red-700">Sin jefe inmediato asignado en tu ficha</span>}</div>
              </div>
            )
          ) : (
            <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
              <div className="md:col-span-2 relative">
                <label className="block text-xs text-gray-600 mb-1" htmlFor="nov-person">Colaborador</label>
                <input id="nov-person" value={peopleQuery} onChange={(e) => { setPeopleQuery(e.target.value); setPeopleOpen(true); }} onFocus={() => setPeopleOpen(true)} className="w-full border rounded-md px-3 py-2 bg-gray-50" placeholder="Buscar por nombre o cédula" />
                {peopleOpen && (
                  <div className="absolute z-20 mt-1 w-full bg-white border rounded-lg shadow max-h-56 overflow-y-auto">
                    {suggestedPeople.length === 0 ? <div className="px-3 py-2 text-sm text-gray-500">Sin coincidencias</div> : suggestedPeople.map((p) => (
                      <button key={`${p.dbId}-${p.dni}`} type="button" onClick={() => { setForm((prev) => ({ ...prev, collaborator_person_id: p.dbId ? String(p.dbId) : '', collaborator_name: p.name, collaborator_dni: p.dni })); setPeopleQuery(`${p.name} · ${p.dni}`); setPeopleOpen(false); }} className="w-full text-left px-3 py-2 hover:bg-gray-50 border-b last:border-b-0">
                        <div className="text-sm">{p.name}</div><div className="text-xs text-gray-500">{p.dni}{p.position ? ` · ${p.position}` : ''}</div>
                      </button>
                    ))}
                  </div>
                )}
              </div>
              <div className="text-sm"><span className="text-gray-500">Jefe inmediato:</span> {form.immediate_boss_name || '—'}</div>
              <div className="text-xs text-gray-500">La firma usada será la registrada en la ficha del colaborador; queda constancia de que la registraste en su nombre.</div>
            </div>
          )}
        </fieldset>

        <fieldset className="rounded-lg border p-3 grid grid-cols-1 md:grid-cols-2 gap-3">
          <legend className="px-1 text-xs font-semibold text-gray-600">B. Información de la solicitud</legend>
          <label className="block md:col-span-2"><span className="block text-xs text-gray-600 mb-1">Tipo de novedad *</span>
            <select value={form.novelty_type} onChange={(e) => setForm((prev) => ({ ...prev, novelty_type: e.target.value }))} className="w-full border rounded-md px-3 py-2 bg-gray-50">
              {typeOptions.map((t) => <option key={t.type_name} value={t.type_name}>{t.type_name}{Number(t.hr_only) === 1 ? ' (Talento Humano)' : ''}</option>)}
            </select>
            {Number(typeCfg?.anticipation_business_days) > 0 && <span className="block text-xs text-gray-500 mt-1">Debe solicitarse con {typeCfg.anticipation_business_days} días hábiles de anticipación (excepto calamidades).</span>}
            {Number(typeCfg?.requires_director) === 1 && <span className="block text-xs text-gray-500 mt-1">Requiere autorización de {typeCfg.director_name || 'director/coordinador'}.</span>}
          </label>
          {isIncapacidad && (
            <label className="block"><span className="block text-xs text-gray-600 mb-1">Tipo de incapacidad *</span>
              <select value={form.incapacity_type} onChange={(e) => setForm((prev) => ({ ...prev, incapacity_type: e.target.value }))} className="w-full border rounded-md px-3 py-2 bg-gray-50"><option value="">Selecciona…</option>{INCAPACITY_TYPE_OPTIONS.map((o) => <option key={o} value={o}>{o}</option>)}</select></label>
          )}
          {isRetiro && (
            <label className="block"><span className="block text-xs text-gray-600 mb-1">Motivo del retiro *</span>
              <select value={form.retiro_motivo} onChange={(e) => setForm((prev) => ({ ...prev, retiro_motivo: e.target.value }))} className="w-full border rounded-md px-3 py-2 bg-gray-50"><option value="">Selecciona…</option>{RETIRO_MOTIVO_OPTIONS.map((o) => <option key={o} value={o}>{o}</option>)}</select></label>
          )}
          <label className="block"><span className="block text-xs text-gray-600 mb-1">{isRetiro ? 'Fecha del retiro *' : 'Fecha de inicio *'}</span>
            <input type="date" value={form.novelty_date_start} onChange={(e) => setForm((prev) => ({ ...prev, novelty_date_start: e.target.value, novelty_date_end: prev.novelty_date_end < e.target.value ? e.target.value : prev.novelty_date_end }))} className="w-full border rounded-md px-3 py-2 bg-gray-50" /></label>
          {!isRetiro && (
            <label className="block"><span className="block text-xs text-gray-600 mb-1">Fecha final</span>
              <input type="date" value={form.novelty_date_end} min={form.novelty_date_start} onChange={(e) => setForm((prev) => ({ ...prev, novelty_date_end: e.target.value }))} className="w-full border rounded-md px-3 py-2 bg-gray-50" /></label>
          )}
          {!isRetiro && isSameDay && (<>
            <label className="block"><span className="block text-xs text-gray-600 mb-1">Hora de inicio</span><input type="time" value={form.start_time} onChange={(e) => setForm((prev) => ({ ...prev, start_time: e.target.value }))} className="w-full border rounded-md px-3 py-2 bg-gray-50" /></label>
            <label className="block"><span className="block text-xs text-gray-600 mb-1">Hora final</span><input type="time" value={form.end_time} onChange={(e) => setForm((prev) => ({ ...prev, end_time: e.target.value }))} className="w-full border rounded-md px-3 py-2 bg-gray-50" /></label>
          </>)}
          {!isRetiro && (
            <div className="md:col-span-2 rounded-lg border bg-gray-50 px-3 py-2 text-sm flex flex-wrap gap-x-6">
              <span><span className="text-gray-500">No. días:</span> <b>{calendarDays}</b></span>
              <span><span className="text-gray-500">No. horas:</span> <b>{minutesToHours(duration.minutes)}</b> <span className="text-xs text-gray-500">({minutesToLabel(duration.minutes)}{isSameDay ? '' : ' · jornada laboral'})</span></span>
            </div>
          )}
          {form.novelty_type === 'Compensatorio' && (
            <div className="md:col-span-2 text-sm rounded-lg border p-2">Saldo de horas extra disponible: <b className="text-green-700">{balanceLoading ? '…' : minutesToHours(balance?.available_minutes ?? 0)} h</b> · se descontará {minutesToHours(duration.minutes)} h</div>
          )}
          <label className="block md:col-span-2"><span className="block text-xs text-gray-600 mb-1">Justificación de la novedad *</span>
            <textarea value={form.reason} maxLength={maxReason} onChange={(e) => setForm((prev) => ({ ...prev, reason: e.target.value }))} rows={4} className="w-full border rounded-md px-3 py-2 bg-gray-50" placeholder="Explica el motivo de la solicitud" />
            <span className="block text-right text-[11px] text-gray-500">{String(form.reason || '').length}/{maxReason}</span></label>
          {needsExtemp && (
            <label className="block md:col-span-2"><span className="block text-xs text-orange-800 mb-1">La solicitud no cumple la anticipación requerida. Explica por qué se presenta con menos anticipación *</span>
              <textarea value={extempMotivo} onChange={(e) => setExtempMotivo(e.target.value)} rows={2} className="w-full border border-orange-300 rounded-md px-3 py-2 bg-orange-50" /></label>
          )}
          <label className="block md:col-span-2"><span className="block text-xs text-gray-600 mb-1">Soportes {Number(typeCfg?.requires_attachment) === 1 ? '*' : '(opcional)'} · PDF, JPG o PNG, máx. 5 MB</span>
            <input type="file" multiple accept="application/pdf,image/jpeg,image/png" onChange={(e) => setFiles(Array.from(e.target.files || []))} className="w-full text-sm" />
            {files.length > 0 && <span className="block text-xs text-gray-500 mt-1">{files.map((f) => f.name).join(', ')}</span>}
          </label>
        </fieldset>

        {selfMode && isFtType && (
          <fieldset className="rounded-lg border p-3">
            <legend className="px-1 text-xs font-semibold text-gray-600">E. Firma del colaborador</legend>
            {profile?.has_signature ? (
              <div className="flex items-center gap-3"><img src={profile.signature_url} alt="Tu firma registrada" className="h-14 border rounded bg-white" /><span className="text-xs text-gray-500">Se usará tu firma registrada en el perfil. Queda guardada con la solicitud al enviarla.</span></div>
            ) : (
              <div className="text-sm text-red-700">No tienes firma registrada en tu ficha. Talento Humano debe cargarla antes de que puedas enviar la solicitud (puedes guardarla como borrador).</div>
            )}
          </fieldset>
        )}

        <div className="flex flex-wrap justify-end gap-2">
          {editing && <button type="button" onClick={() => { resetForm(); setView('mine'); }} className="px-4 py-2 rounded-md text-sm border">Cancelar edición</button>}
          <button type="button" onClick={() => save(false)} disabled={saving} className="px-4 py-2 rounded-md text-sm border bg-white disabled:opacity-60">Guardar borrador</button>
          <button type="button" onClick={() => save(true)} disabled={saving || (selfMode && isFtType && profileLoaded && !profile?.has_signature)} className="px-4 py-2 rounded-md text-sm text-white bg-gray-800 hover:bg-black disabled:bg-gray-300 disabled:text-gray-600 disabled:cursor-not-allowed">{saving ? 'Procesando…' : 'Enviar solicitud'}</button>
        </div>
      </div>
    );
  };

  return (
    <div className="space-y-4">
      <div>
        <h4 className="text-base font-semibold">Novedades de personal</h4>
        <p className="text-sm text-gray-600">Solicitudes FT-GTH-022: permisos, licencias, calamidades, compensatorios y más, con aprobación del jefe inmediato y recibido de Talento Humano.</p>
      </div>
      <div className="flex gap-1 overflow-x-auto border-b" role="tablist">
        {tabs.map(([k, label, n]) => (
          <button key={k} type="button" role="tab" aria-selected={view === k} onClick={() => { if (k !== 'new' && editing) resetForm(); setView(k); }}
            className={`whitespace-nowrap px-3 py-2 text-sm border-b-2 ${view === k ? 'border-gray-800 font-semibold text-gray-900' : 'border-transparent text-gray-600 hover:text-gray-900'}`}>
            {label}{n ? <span className="ml-1.5 rounded-full bg-amber-100 text-amber-800 px-1.5 text-xs">{n}</span> : null}
          </button>
        ))}
      </div>
      {feedback.text && view !== 'new' && (
        <div role="status" className={`rounded-lg border px-3 py-2 text-sm ${feedback.type === 'ok' ? 'bg-green-50 text-green-700 border-green-200' : 'bg-red-50 text-red-700 border-red-200'}`}>{feedback.text}</div>
      )}
      {view === 'new' && renderForm()}
      {view === 'config' && meta.isHr && <NoveltyTypeConfigView types={meta.types} onSaved={loadMeta} />}
      {['mine', 'pending', 'history'].includes(view) && renderList()}

      {detailId && <NoveltyDetailModal id={detailId} onClose={() => setDetailId(null)} onPdf={downloadPdf} canComment={meta.canReceive} onChanged={refreshAll} />}
      {approveTarget && <NoveltyApproveModal row={approveTarget} onClose={() => setApproveTarget(null)} onDone={afterAction} />}
      {rejectTarget && <NoveltyBossRejectModal row={rejectTarget} onClose={() => setRejectTarget(null)} onDone={afterAction} />}
      {returnTarget && <NoveltyReturnModal row={returnTarget} onClose={() => setReturnTarget(null)} onDone={afterAction} />}
      {directorTarget && <NoveltyDirectorModal row={directorTarget} onClose={() => setDirectorTarget(null)} onDone={afterAction} />}
      {hrReceiveTarget && <NoveltyHrReceiveModal row={hrReceiveTarget} onClose={() => setHrReceiveTarget(null)} onDone={afterAction} />}
      {hrRejectTarget && <NoveltyHrRejectModal row={hrRejectTarget} onClose={() => setHrRejectTarget(null)} onDone={afterAction} />}
    </div>
  );
}
